import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/features/auth/contexts/EnhancedAuthContext';
import { profileAPI } from '@/lib/supabase/profile';
import { Bell, Mail, MessageSquare, Clock, Save, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

export const NotificationPreferences: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    // Email Notifications
    email_notifications: true,
    email_recall_alerts: true,
    email_campaign_updates: true,
    email_system_notifications: true,
    email_weekly_reports: false,

    // SMS Notifications
    sms_notifications: false,
    sms_urgent_alerts: true,
    sms_phone_number: '',

    // In-App Notifications
    in_app_notifications: true,
    in_app_sound: true,
    in_app_badge: true,

    // Notification Frequency
    notification_frequency: 'immediate',
    quiet_hours_start: '',
    quiet_hours_end: '',
    quiet_hours_enabled: false
  });

  useEffect(() => {
    if (user?.id) {
      loadPreferences();
    }
  }, [user?.id]);

  const loadPreferences = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      const prefs = await profileAPI.getNotificationPreferences(user.id);

      if (prefs) {
        setPreferences(prefs);
        setFormData({
          email_notifications: prefs.email_notifications,
          email_recall_alerts: prefs.email_recall_alerts,
          email_campaign_updates: prefs.email_campaign_updates,
          email_system_notifications: prefs.email_system_notifications,
          email_weekly_reports: prefs.email_weekly_reports,
          sms_notifications: prefs.sms_notifications,
          sms_urgent_alerts: prefs.sms_urgent_alerts,
          sms_phone_number: prefs.sms_phone_number || '',
          in_app_notifications: prefs.in_app_notifications,
          in_app_sound: prefs.in_app_sound,
          in_app_badge: prefs.in_app_badge,
          notification_frequency: prefs.notification_frequency,
          quiet_hours_start: prefs.quiet_hours_start || '',
          quiet_hours_end: prefs.quiet_hours_end || '',
          quiet_hours_enabled: prefs.quiet_hours_enabled
        });
      }
    } catch (error) {
      toast.error('Failed to load notification preferences');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!user?.id) return;

    try {
      setSaving(true);

      const result = await profileAPI.updateNotificationPreferences(user.id, formData);

      if (result) {
        setPreferences(result);
        toast.success('Notification preferences saved successfully');
      } else {
        toast.error('Failed to save notification preferences');
      }
    } catch (error) {
      toast.error('Failed to save notification preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className='container mx-auto p-6'>
        <div className='animate-pulse'>
          <div className='h-8 bg-gray-200 rounded w-1/4 mb-6' />
          <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
            <div className='h-64 bg-gray-200 rounded' />
            <div className='h-64 bg-gray-200 rounded' />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='container mx-auto p-6 space-y-6'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-4'>
          <Button variant='ghost' size='sm' onClick={() => navigate('/profile')}>
            <ArrowLeft className='h-4 w-4 mr-2' />
            Back to Profile
          </Button>
          <div>
            <h1 className='text-3xl font-bold'>Notification Preferences</h1>
            <p className='text-muted-foreground'>Manage how you receive notifications</p>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? (
            <>
              <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2' />
              Saving...
            </>
          ) : (
            <>
              <Save className='h-4 w-4 mr-2' />
              Save Changes
            </>
          )}
        </Button>
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
        {/* Email Notifications */}
        <Card>
          <CardHeader>
            <CardTitle className='flex items-center gap-2'>
              <Mail className='h-5 w-5' />
              Email Notifications
            </CardTitle>
            <CardDescription>Configure your email notification preferences</CardDescription>
          </CardHeader>
          <CardContent className='space-y-4'>
            <div className='flex items-center justify-between'>
              <div>
                <Label htmlFor='email_notifications'>Email Notifications</Label>
                <p className='text-sm text-muted-foreground'>Receive notifications via email</p>
              </div>
              <Switch
                id='email_notifications'
                checked={formData.email_notifications}
                onCheckedChange={checked => handleInputChange('email_notifications', checked)}
              />
            </div>

            {formData.email_notifications && (
              <div className='space-y-4 pl-4 border-l-2 border-muted'>
                <div className='flex items-center justify-between'>
                  <div>
                    <Label htmlFor='email_recall_alerts'>Recall Alerts</Label>
                    <p className='text-sm text-muted-foreground'>Important recall notifications</p>
                  </div>
                  <Switch
                    id='email_recall_alerts'
                    checked={formData.email_recall_alerts}
                    onCheckedChange={checked => handleInputChange('email_recall_alerts', checked)}
                  />
                </div>

                <div className='flex items-center justify-between'>
                  <div>
                    <Label htmlFor='email_campaign_updates'>Campaign Updates</Label>
                    <p className='text-sm text-muted-foreground'>Updates on campaign progress</p>
                  </div>
                  <Switch
                    id='email_campaign_updates'
                    checked={formData.email_campaign_updates}
                    onCheckedChange={checked =>
                      handleInputChange('email_campaign_updates', checked)
                    }
                  />
                </div>

                <div className='flex items-center justify-between'>
                  <div>
                    <Label htmlFor='email_system_notifications'>System Notifications</Label>
                    <p className='text-sm text-muted-foreground'>
                      System updates and announcements
                    </p>
                  </div>
                  <Switch
                    id='email_system_notifications'
                    checked={formData.email_system_notifications}
                    onCheckedChange={checked =>
                      handleInputChange('email_system_notifications', checked)
                    }
                  />
                </div>

                <div className='flex items-center justify-between'>
                  <div>
                    <Label htmlFor='email_weekly_reports'>Weekly Reports</Label>
                    <p className='text-sm text-muted-foreground'>Summary reports and analytics</p>
                  </div>
                  <Switch
                    id='email_weekly_reports'
                    checked={formData.email_weekly_reports}
                    onCheckedChange={checked => handleInputChange('email_weekly_reports', checked)}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* SMS Notifications */}
        <Card>
          <CardHeader>
            <CardTitle className='flex items-center gap-2'>
              <MessageSquare className='h-5 w-5' />
              SMS Notifications
            </CardTitle>
            <CardDescription>Configure your SMS notification preferences</CardDescription>
          </CardHeader>
          <CardContent className='space-y-4'>
            <div className='flex items-center justify-between'>
              <div>
                <Label htmlFor='sms_notifications'>SMS Notifications</Label>
                <p className='text-sm text-muted-foreground'>Receive notifications via SMS</p>
              </div>
              <Switch
                id='sms_notifications'
                checked={formData.sms_notifications}
                onCheckedChange={checked => handleInputChange('sms_notifications', checked)}
              />
            </div>

            {formData.sms_notifications && (
              <div className='space-y-4 pl-4 border-l-2 border-muted'>
                <div>
                  <Label htmlFor='sms_phone_number'>Phone Number</Label>
                  <Input
                    id='sms_phone_number'
                    type='tel'
                    value={formData.sms_phone_number}
                    onChange={e => handleInputChange('sms_phone_number', e.target.value)}
                    placeholder='+1 (555) 123-4567'
                    className='mt-2'
                  />
                </div>

                <div className='flex items-center justify-between'>
                  <div>
                    <Label htmlFor='sms_urgent_alerts'>Urgent Alerts Only</Label>
                    <p className='text-sm text-muted-foreground'>Only critical notifications</p>
                  </div>
                  <Switch
                    id='sms_urgent_alerts'
                    checked={formData.sms_urgent_alerts}
                    onCheckedChange={checked => handleInputChange('sms_urgent_alerts', checked)}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* In-App Notifications */}
        <Card>
          <CardHeader>
            <CardTitle className='flex items-center gap-2'>
              <Bell className='h-5 w-5' />
              In-App Notifications
            </CardTitle>
            <CardDescription>Configure in-app notification settings</CardDescription>
          </CardHeader>
          <CardContent className='space-y-4'>
            <div className='flex items-center justify-between'>
              <div>
                <Label htmlFor='in_app_notifications'>In-App Notifications</Label>
                <p className='text-sm text-muted-foreground'>Show notifications in the app</p>
              </div>
              <Switch
                id='in_app_notifications'
                checked={formData.in_app_notifications}
                onCheckedChange={checked => handleInputChange('in_app_notifications', checked)}
              />
            </div>

            {formData.in_app_notifications && (
              <div className='space-y-4 pl-4 border-l-2 border-muted'>
                <div className='flex items-center justify-between'>
                  <div>
                    <Label htmlFor='in_app_sound'>Notification Sound</Label>
                    <p className='text-sm text-muted-foreground'>Play sound for notifications</p>
                  </div>
                  <Switch
                    id='in_app_sound'
                    checked={formData.in_app_sound}
                    onCheckedChange={checked => handleInputChange('in_app_sound', checked)}
                  />
                </div>

                <div className='flex items-center justify-between'>
                  <div>
                    <Label htmlFor='in_app_badge'>Badge Count</Label>
                    <p className='text-sm text-muted-foreground'>Show notification count badge</p>
                  </div>
                  <Switch
                    id='in_app_badge'
                    checked={formData.in_app_badge}
                    onCheckedChange={checked => handleInputChange('in_app_badge', checked)}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Notification Frequency & Quiet Hours */}
        <Card>
          <CardHeader>
            <CardTitle className='flex items-center gap-2'>
              <Clock className='h-5 w-5' />
              Frequency & Timing
            </CardTitle>
            <CardDescription>Control when and how often you receive notifications</CardDescription>
          </CardHeader>
          <CardContent className='space-y-4'>
            <div>
              <Label htmlFor='notification_frequency'>Notification Frequency</Label>
              <Select
                value={formData.notification_frequency}
                onValueChange={value => handleInputChange('notification_frequency', value)}
              >
                <SelectTrigger className='mt-2'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='immediate'>Immediate</SelectItem>
                  <SelectItem value='daily'>Daily Digest</SelectItem>
                  <SelectItem value='weekly'>Weekly Summary</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className='flex items-center justify-between'>
              <div>
                <Label htmlFor='quiet_hours_enabled'>Quiet Hours</Label>
                <p className='text-sm text-muted-foreground'>
                  Pause notifications during specific hours
                </p>
              </div>
              <Switch
                id='quiet_hours_enabled'
                checked={formData.quiet_hours_enabled}
                onCheckedChange={checked => handleInputChange('quiet_hours_enabled', checked)}
              />
            </div>

            {formData.quiet_hours_enabled && (
              <div className='grid grid-cols-2 gap-4 pl-4 border-l-2 border-muted'>
                <div>
                  <Label htmlFor='quiet_hours_start'>Start Time</Label>
                  <Input
                    id='quiet_hours_start'
                    type='time'
                    value={formData.quiet_hours_start}
                    onChange={e => handleInputChange('quiet_hours_start', e.target.value)}
                    className='mt-2'
                  />
                </div>
                <div>
                  <Label htmlFor='quiet_hours_end'>End Time</Label>
                  <Input
                    id='quiet_hours_end'
                    type='time'
                    value={formData.quiet_hours_end}
                    onChange={e => handleInputChange('quiet_hours_end', e.target.value)}
                    className='mt-2'
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
